import { createFileRoute, useRouteContext, redirect } from "@tanstack/react-router";
import { type DocumentType, graphql } from "@repo/graphql-types";
import Left from "@repo/icons/default/Left";
import Register from "@repo/icons/default/Register";
import { useGraphQL, queryFn } from "@/hooks/use-graphql";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Page } from "@/components/ui/layout/page/page";
import {
  EmptyStateRoot,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "@/components/ui/empty-state/empty-state";
import { CardDetails } from "@/components/ui/card/card-details";
import { LivanceApiPaymentMethod } from "@/lib/enums/livance-api-payment-methods-enum";

import type { JSX } from "react";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const CardManagementPageQuery = graphql(/* GraphQL */ `
  query CardManagementPageQuery($codUsuario: Int!) {
    ...CardDetailsQueryFragment
    usuariosProfissionais: usuariosProfissionais_by_pk(codUsuario: $codUsuario) {
      formaDePagamento
    }
    usuariosCartoes(where: { ativo: { _eq: true } }) {
      ativo
    }
  }
`);

const CardManagementPage = (): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });

  const queryResult = useGraphQL(CardManagementPageQuery, {
    codUsuario: user.codUsuario,
  });
  const { data } = queryResult;

  const hasCard = Boolean(data?.usuariosCartoes && data.usuariosCartoes.length > 0);
  const isConfiguredToCard = Boolean(
    data?.usuariosProfissionais?.formaDePagamento === LivanceApiPaymentMethod.CreditCard,
  );

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Gerenciar cartão" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {!isConfiguredToCard && !hasCard ? (
            <EmptyStateRoot>
              <EmptyStateIcon icon={Register} />
              <EmptyStateTitle>
                Sua conta está configurada para pagamento via boleto
              </EmptyStateTitle>
              <EmptyStateDescription>
                Para alterar a forma de pagamento consulte nosso time de relacionamento
              </EmptyStateDescription>
            </EmptyStateRoot>
          ) : (
            // biome-ignore lint/complexity/noUselessFragments: legacy code
            <>{data ? <CardDetails data={data} /> : null}</>
          )}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/profile/card/")({
  component: CardManagementPage,
  loader: async (opts) => {
    try {
      const data = await opts.context.queryClient.ensureQueryData<
        DocumentType<typeof CardManagementPageQuery>
      >({
        queryKey: [
          "CardManagementPageQuery",
          {
            codUsuario: opts.context.user.codUsuario,
          },
        ],
        queryFn: () =>
          queryFn(CardManagementPageQuery, opts.context.authClient, {
            codUsuario: opts.context.user.codUsuario,
          }),
      });

      const hasCard = Boolean(data.usuariosCartoes.length > 0);
      const isConfiguredToCard = Boolean(
        data.usuariosProfissionais?.formaDePagamento ===
          LivanceApiPaymentMethod.CreditCard,
      );

      if (isConfiguredToCard && !hasCard) throw new Error("user does not have a card");
    } catch (_e) {
      redirect({
        to: "/settings/profile/card/create",
        throw: true,
      });
    }
  },
});
